import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import '../styles/Auth.css';
import '../styles/Buttons.css';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{ email?: string; general?: string }>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (errors.email) {
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setLoading(true);
      setErrors({});
      await authAPI.forgotPassword(email);
      setSuccess(true);
    } catch (error: any) {
      setErrors({
        general: error.response?.data?.message || 'Failed to send reset link. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-logo">
            <h1>EnHomeBuilder</h1>
            <p>Build better, faster</p>
          </div>

          <h2 className="auth-title">Check your email</h2>
          <p className="auth-subtitle">We've sent a password reset link to {email}</p>

          <div className="auth-success">
            If an account exists with this email, you'll receive a password reset link shortly.
          </div>

          <div className="auth-footer" style={{ marginTop: '32px' }}>
            Didn't receive the email?{' '}
            <button
              onClick={() => {
                setSuccess(false);
                setEmail('');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#48182F',
                fontWeight: 500,
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0,
                font: 'inherit'
              }}
            >
              Try again
            </button>
          </div>

          <div className="auth-footer">
            <Link to="/login">Back to sign in</Link>
          </div>

          <div className="auth-powered">
            Powered by EnHomeBuilder
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-logo">
          <h1>EnHomeBuilder</h1>
          <p>Build better, faster</p>
        </div>

        <h2 className="auth-title">Reset your password</h2>
        <p className="auth-subtitle">
          Enter your email address and we'll send you a link to reset your password
        </p>

        <form className="auth-form" onSubmit={handleSubmit}>
          {errors.general && (
            <div className="form-error" style={{ marginBottom: '8px' }}>
              {errors.general}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className={`form-input ${errors.email ? 'error' : ''}`}
              placeholder="you@example.com"
              value={email}
              onChange={handleChange}
              disabled={loading}
              autoComplete="email"
            />
            {errors.email && <span className="form-error">{errors.email}</span>}
          </div>

          <div className="form-submit">
            <button
              type="submit"
              className={`btn btn-primary btn-lg btn-full ${loading ? 'btn-loading' : ''}`}
              disabled={loading}
            >
              {loading ? '' : 'Send reset link'}
            </button>
          </div>
        </form>

        <div className="auth-footer">
          Remember your password?{' '}
          <Link to="/login">Sign in</Link>
        </div>

        <div className="auth-powered">
          Powered by EnHomeBuilder
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
